import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  EventService,
  IJobScreenEvent,
  IRemoveSupplier,
  JobsDeploymentService,
  JobsService,
  JobUnassignedReasonService,
  OrderService,
  UserService,
} from '../../../../services';
import {
  VMEJobScreenSupplierInfoJob,
  VMEJobScreenSupplierInfoJobAssignment,
} from '../../../../interfaces/screen-specific/job-screen';
import { JobStatus } from '../../../../enums/job-status';
import { Router } from '@angular/router';
import { OrganisationTypeEnum } from '../../../../enums/organisation-types';
import { JobScreenCommonService } from '../../common/job-screen-common.service';
import { IUser } from '../../../../interfaces/user';
import { Subscription } from 'rxjs';
import { RefreshJobEventTypeEnum } from '../../../../enums/refresh-job-event-type.enum';
import { MessageService } from 'primeng';
import { Message } from '../../../../classes/Message';
import { RequestChannelType } from '../../../../enums/request-channel-type';

@Component({
  selector: 'app-vme-job-screen-supplier-info',
  templateUrl: './vme-job-screen-supplier-info.component.html',
  styleUrls: [
    './vme-job-screen-supplier-info.component.scss',
    '../../common/job-screen-common.scss',
    '../../common/common-scss/job-screen-supplier-info-common.scss',
  ],
})
export class VMEJobScreenSupplierInfoComponent implements OnInit, OnDestroy {
  private _jobId: number;
  organisationTypeId = 1; // Implied by virtue of being a VME screen
  loadingJob: boolean = false;
  job: VMEJobScreenSupplierInfoJob;
  selectedImpersonateUser: null;
  impersonateSupplierUsers: any;
  showImpersonateSupplierDialog: boolean;
  unassignReasons: { label: string; value: number }[];
  showRemoveConfirm: boolean;
  unassignReasonId: number;
  unassignComment: string;
  isImpersonating: boolean = false;

  subscriptions: Subscription[] = [];

  @Input('jobId')
  set jobId(jobId: number) {
    this._jobId = jobId;
    this.initData();
  }

  get jobId(): number {
    return this._jobId;
  }

  constructor(
    private jobService: JobsService,
    private eventService: EventService,
    private jobUnassignedReasonService: JobUnassignedReasonService,
    private orderService: OrderService,
    private router: Router,
    private jobDeploymentService: JobsDeploymentService,
    private jobScreenCommonService: JobScreenCommonService,
    private messageService: MessageService,
    private userService: UserService,
  ) {
    this.subscriptions.push(
      this.eventService.JobScreenEvent.subscribe((event: IJobScreenEvent) => {
        this.handleEvents(event);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((x) => x?.unsubscribe());
  }

  async ngOnInit() {
    this.isImpersonating = (await this.userService.isUserImpersonating()).isImpersonating;
  }

  initData() {
    this.getJob(this.jobId);
  }

  getJob(jobId: number) {
    this.loadingJob = true;
    this.jobService
      .getForComponent(jobId, 'vme-job-screen-supplier-info')
      .then((supplier: VMEJobScreenSupplierInfoJob) => {
        this.job = supplier;
        this.loadingJob = false;
      });
  }

  private handleEvents(event: IJobScreenEvent) {
    if (event.event == 'job-details-changed') {
      this.getJob(this.jobId);
    }
    if (event.event == 'refresh-supplier') {
      this.getJob(this.jobId);
    }
    if (event.event == 'vehicle-updated' && !this.job?.supplier) {
      this.getJob(this.jobId);
    }
    if (event.event == 'task-type-changed' && !this.job?.supplier) {
      this.getJob(this.jobId);
    }
  }

  isJobCompleted(job: VMEJobScreenSupplierInfoJob): boolean {
    if (!job) {
      return false;
    }

    return (
      job.statusId == JobStatus.Completed ||
      job.statusId == JobStatus.Cancelled ||
      job.statusId == JobStatus.Closed
    );
  }

  impersonateSupplierConfirm() {
    this.showImpersonateSupplierDialog = false;
    this.jobScreenCommonService.impersonateUser(this.selectedImpersonateUser);
  }

  impersonateSupplier() {
    this.selectedImpersonateUser = null;

    this.orderService.getImpersonateSupplierUsers(this.jobId).then((res) => {
      this.impersonateSupplierUsers = this.usersToSelectItems(res);
    });

    this.showImpersonateSupplierDialog = true;
  }

  usersToSelectItems(l) {
    let users = [];
    l.forEach((o: IUser) => {
      users.push({
        label:
          o.firstName || o.lastName
            ? ((o.firstName ? o.firstName : '') + ' ' + (o.lastName ? o.lastName : '')).trim()
            : o.email,
        value: o.id,
      });
    });
    return users;
  }

  removeFromSupplier() {
    this.jobUnassignedReasonService.getReasons({ isActive: true }).then((x) => {
      this.unassignReasons = x.map((y, z) => {
        return { label: y.description, value: y.id };
      });

      this.showRemoveConfirm = true;
    });
  }

  async confirmRemoveFromSupplier() {
    let removeSupplierInfo: IRemoveSupplier = {
      id: this.job.id,
      jobNumber: this.job.jobNumber,
      supplierId: this.job.supplier?.id,
      jobUnassignedComment: this.unassignComment,
      jobUnassignedReasonId: this.unassignReasonId,
    };

    try {
      await this.jobService.removeJobSupplier(removeSupplierInfo);
    } catch (error) {
    } finally {
      this.showRemoveConfirm = false;
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    }
  }

  goToJobDeployment(job: VMEJobScreenSupplierInfoJob) {
    this.router.navigate(['/app/job-deployment/' + job.id]);
  }

  protected readonly organisationTypeEnum = OrganisationTypeEnum;

  previousAssignments(job: VMEJobScreenSupplierInfoJob): VMEJobScreenSupplierInfoJobAssignment[] {
    if (job != null && job.jobAssignments != null) {
      return job.jobAssignments.filter((x) => x.unassigned == true);
    }
  }
}
